import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Link } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interSemiBold, interRegular } from '../../lib/fonts';
import { responsive } from '../../lib/responsive';
import { buildCanonicalDashboardPath } from '../../lib/dashboardRoutes';
import Button, { PaperFeedButton, ProjectSettingsButton } from '../ui/Button';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useAppLocale } from '../../lib/i18n/AppI18nProvider';

interface DigestIssueHeaderProps {
    digestName: string;
    issueDate: string;
    papersCount: number;
    status: string | null;
    emailSent: boolean;
    projectId: string | null;
    isOwner: boolean;
    isPublic: boolean;
    onSharePress?: () => void;
}

/**
 * DigestIssueHeader displays metadata about a digest issue
 * Shows the digest name, issue date, paper count, and status
 */
export default function DigestIssueHeader({
    digestName,
    issueDate,
    papersCount,
    status,
    emailSent,
    projectId,
    isOwner,
    isPublic,
    onSharePress,
}: DigestIssueHeaderProps) {
    const { fontsLoaded } = useFontLoading();
    const { i18n } = useLingui();
    const { formatLocale } = useAppLocale();

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(formatLocale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getStatusColor = () => {
        switch (status) {
            case 'sent':
                return emailSent ? colors.primaryGreen : colors.text500;
            case 'sending':
                return colors.action;
            case 'pending':
                return colors.text500;
            case 'failed':
                return colors.errorRed;
            default:
                return colors.text700;
        }
    };

    const getStatusLabel = () => {
        switch (status) {
            case 'sent':
                return emailSent
                    ? i18n._(msg({ id: 'digest.status.sent', message: 'Sent' }))
                    : i18n._(msg({ id: 'digest.status.appOnly', message: 'App only' }));
            case 'sending':
                return i18n._(msg({ id: 'digest.status.sending', message: 'Sending' }));
            case 'pending':
                return i18n._(msg({ id: 'digest.status.pending', message: 'Pending' }));
            case 'failed':
                return i18n._(msg({ id: 'digest.status.failed', message: 'Failed' }));
            default:
                return i18n._(msg({ id: 'digest.status.unknown', message: 'Unknown' }));
        }
    };

    return (
        <View style={styles.container}>
            {/* Digest name with action button */}
            <View style={styles.titleRow} {...responsive('digest-header-title-row')}>
                <View style={styles.titleContainer}>
                    <Text
                        style={[
                            styles.digestName,
                            fontsLoaded && interSemiBold
                        ]}
                        {...responsive('digest-header-title')}
                    >
                        {digestName}
                    </Text>
                    <Text
                        style={[
                            styles.subtitle,
                            fontsLoaded && interRegular
                        ]}
                    ><Trans id="digest.digestissueheader.manageYourResearchDigests" comment="UI copy in components/digest/DigestIssueHeader.tsx">
                        Manage your research digests
                    </Trans></Text>
                </View>

                {/* Action buttons based on ownership */}
                <View style={styles.buttonsContainer} {...responsive('digest-header-buttons')}>
                    {projectId && isOwner && (
                        <>
                            <Link href={buildCanonicalDashboardPath(`/${projectId}/settings`)} asChild>
                                <ProjectSettingsButton
                                    variant="secondary"
                                    style={styles.actionButton}
                                    textStyle={styles.actionButtonText}
                                    iconSize={14}
                                    activeOpacity={0.7}
                                />
                            </Link>
                            <Link href={buildCanonicalDashboardPath(`/${projectId}`)} asChild>
                                <PaperFeedButton
                                    variant="secondary"
                                    style={styles.actionButton}
                                    textStyle={styles.actionButtonText}
                                    iconSize={14}
                                    activeOpacity={0.7}
                                />
                            </Link>
                        </>
                    )}
                    {(isOwner || isPublic) && onSharePress && (
                        <Button
                            label={msg({ id: "digest.digestissueheader.share", message: "Share", comment: "UI label in components/digest/DigestIssueHeader.tsx" })}
                            iconName="share-nodes"
                            variant={isOwner ? 'primary' : 'secondary'}
                            style={isOwner ? styles.shareButtonOwner : styles.actionButton}
                            textStyle={isOwner ? styles.shareButtonOwnerText : styles.actionButtonText}
                            iconSize={16}
                            activeOpacity={0.7}
                            onPress={onSharePress}
                        />
                    )}
                </View>
            </View>

            {/* Issue metadata row */}
            <View style={styles.metadataRow}>
                {/* Date */}
                <View style={styles.metadataItem}>
                    <MaterialIcons name="calendar-today" size={16} color={colors.text700} />
                    <Text
                        style={[
                            styles.metadataText,
                            fontsLoaded && interRegular
                        ]}
                    >
                        {formatDate(issueDate)}
                    </Text>
                </View>

                {/* Paper count */}
                <View style={styles.metadataItem}>
                    <MaterialIcons name="article" size={16} color={colors.text700} />
                    <Text
                        style={[
                            styles.metadataText,
                            fontsLoaded && interRegular
                        ]}
                    >
                        {papersCount} {papersCount === 1 ? <Trans id="digest.digestissueheader.paper" comment="Conditional UI copy in components/digest/DigestIssueHeader.tsx">paper</Trans> : <Trans id="digest.digestissueheader.papers" comment="Conditional UI copy in components/digest/DigestIssueHeader.tsx">papers</Trans>}
                    </Text>
                </View>

                {/* Status badge */}
                {status && (
                    <View style={[styles.statusBadge, { borderColor: getStatusColor() }]}>
                        <View style={[styles.statusDot, { backgroundColor: getStatusColor() }]} />
                        <Text
                            style={[
                                styles.statusText,
                                { color: getStatusColor() },
                                fontsLoaded && interSemiBold
                            ]}
                        >
                            {getStatusLabel()}
                        </Text>
                    </View>
                )}
            </View>

            {/* Divider */}
            <View style={styles.divider} />
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        paddingBottom: 24,
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: 16,
        gap: 16,
    },
    titleContainer: {
        flex: 1,
        minWidth: 0,
        maxWidth: '100%',
        gap: 4,
    },
    digestName: {
        fontSize: 28,
        fontWeight: '600',
        lineHeight: 36,
        color: colors.primaryGreen,
    },
    subtitle: {
        fontSize: 14,
        lineHeight: 20,
        color: colors.text700,
    },
    buttonsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    actionButton: {
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 8,
    },
    actionButtonText: {
        fontSize: 14,
    },
    shareButtonOwner: {
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 8,
        backgroundColor: colors.action,
    },
    shareButtonOwnerText: {
        fontSize: 14,
    },
    metadataRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 16,
    },
    metadataItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    metadataText: {
        fontSize: 14,
        color: colors.text700,
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        borderWidth: 1,
    },
    statusDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
    },
    statusText: {
        fontSize: 12,
        fontWeight: '600',
        textTransform: 'capitalize',
    },
    divider: {
        height: 1,
        backgroundColor: colors.border,
        marginTop: 20,
    },
});
